/**
 * Извлечение частых сочетаний из словаря.
 *
 * Биграммы и триграммы — самостоятельные учебные единицы: моторная память
 * формируется на сочетаниях, а не на отдельных буквах (ТЗ §1).
 */

import type { LanguageCode } from '@type2x/shared-contracts';

import { getWords } from './registry.js';

/** Сочетание символов и его вес. */
export interface NgramEntry {
  readonly text: string;
  /** Нормализованная частота, 0..1. */
  readonly frequency: number;
}

function extract(language: LanguageCode, size: number): NgramEntry[] {
  const weights = new Map<string, number>();

  for (const word of getWords(language)) {
    const characters = [...word.text.toLowerCase()];
    for (let index = 0; index + size <= characters.length; index += 1) {
      const gram = characters.slice(index, index + size).join('');
      weights.set(gram, (weights.get(gram) ?? 0) + word.frequency);
    }
  }

  const maximum = Math.max(...weights.values(), 1);
  return [...weights.entries()]
    .map(([text, weight]) => ({ text, frequency: weight / maximum }))
    .sort((a, b) => b.frequency - a.frequency);
}

const CACHE = new Map<string, readonly NgramEntry[]>();

function cached(language: LanguageCode, size: number): readonly NgramEntry[] {
  const key = `${language}:${size}`;
  const hit = CACHE.get(key);
  if (hit !== undefined) return hit;
  const built = extract(language, size);
  CACHE.set(key, built);
  return built;
}

/** Частые биграммы языка, по убыванию частоты. */
export function getBigrams(language: LanguageCode, limit?: number): readonly NgramEntry[] {
  const all = cached(language, 2);
  return limit === undefined ? all : all.slice(0, limit);
}

/** Частые триграммы языка, по убыванию частоты. */
export function getTrigrams(language: LanguageCode, limit?: number): readonly NgramEntry[] {
  const all = cached(language, 3);
  return limit === undefined ? all : all.slice(0, limit);
}

/**
 * Частота отдельных символов языка, по убыванию.
 *
 * Это та же выборка, что и у сочетаний, только длиной в один символ: режим
 * «Основы» показывает клавиши, а вес клавиши в реальном языке считается по
 * тому же словарю, что и всё остальное.
 */
export function getCharacterFrequencies(language: LanguageCode): readonly NgramEntry[] {
  return cached(language, 1);
}

const BY_CHARACTER = new Map<LanguageCode, ReadonlyMap<string, number>>();

/** Нормализованная частота одного символа, 0..1. Незнакомый символ — 0. */
export function characterFrequency(language: LanguageCode, character: string): number {
  let index = BY_CHARACTER.get(language);
  if (index === undefined) {
    index = new Map(
      getCharacterFrequencies(language).map((entry) => [entry.text, entry.frequency]),
    );
    BY_CHARACTER.set(language, index);
  }
  return index.get(character.toLowerCase()) ?? 0;
}
